import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Upload,
  FileText,
  AlertCircle,
  Trophy,
  MapPin,
  User as UserIcon,
  Users,
  Shield,
  Copy,
  Check,
  History,
  ExternalLink,
  Trash2,
  X,
  type LucideIcon,
} from "../components/icons";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BatchDetailsModal } from "../components/ingestion/BatchDetailsModal";
import { ConfirmationModal } from "../components/ConfirmationModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  deleteBatch,
  listBatches,
  type IngestionBatchSummary,
} from "../lib/ingestion";
const DataIngestionDialog = lazy(
  () => import("../components/DataIngestionDialog"),
);
import {
  downloadCompetitionTemplate,
  downloadVenueTemplate,
  downloadRefereeTemplate,
  downloadPlayerTemplate,
  downloadPlayerWithTeamTemplate,
  downloadTeamTemplate,
  downloadBulkTemplate,
} from "../lib/export";
import { copyTextToClipboard } from "../lib/clipboard";

type ModelType =
  | "competitions"
  | "venues"
  | "referees"
  | "players"
  | "players_with_team"
  | "teams"
  | "bulk";

interface DataModel {
  id: ModelType;
  label: string;
  description: string;
  icon: LucideIcon;
  fields: string[];
  aiPrompt: string;
  downloadTemplate: (format: "csv" | "json") => void;
}

/** Radix `Select.Item` forbids an empty-string value (reserved for
 *  "nothing selected") — same sentinel pattern as `ReferenceValueSelect`. */
const UNSET = "__unset__";

const MODEL_FILTER_LABELS: Record<string, string> = {
  "": "All Models",
  competitions: "Competitions",
  venues: "Venues",
  referees: "Referees",
  players: "Players",
  teams: "Teams",
};

const STATUS_FILTER_LABELS: Record<string, string> = {
  "": "All Statuses",
  queued: "Queued",
  in_progress: "Processing",
  conflicts: "Has Conflicts",
  success: "Success",
  failed: "Failed",
};

export default function IngestionManager() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"upload" | "batches">("upload");
  const [selectedModel, setSelectedModel] = useState<ModelType | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [modelFilter, setModelFilter] = useState<string>("");
  const [selectedBatch, setSelectedBatch] =
    useState<IngestionBatchSummary | null>(null);
  const [pendingDeleteBatch, setPendingDeleteBatch] =
    useState<IngestionBatchSummary | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [bannerMessage, setBannerMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const closeDeleteDialog = useCallback(() => setPendingDeleteBatch(null), []);
  useEffect(() => {
    if (activeTab !== "batches" && bannerMessage) {
      setBannerMessage(null);
    }
  }, [activeTab, bannerMessage]);

  useEffect(() => {
    if (!bannerMessage) {
      return;
    }

    const timeoutId = window.setTimeout(() => setBannerMessage(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [bannerMessage]);

  // Fetch batches list
  const {
    data: batchesData,
    isLoading: batchesLoading,
    refetch,
  } = useQuery({
    queryKey: ["batches", page, statusFilter, modelFilter],
    queryFn: () =>
      listBatches({
        page,
        page_size: 20,
        status: statusFilter || undefined,
        target_model: modelFilter || undefined,
      }),
    enabled: activeTab === "batches",
  });

  const models: DataModel[] = [
    {
      id: "competitions",
      label: t("ingestion.competitions"),
      description: t("ingestion.competitionsDesc"),
      icon: Trophy,
      fields: ["name", "short_name", "gender", "country_name"],
      aiPrompt: `Transform the following competition data into CSV format with these exact columns: name, short_name, gender, country_name.

Requirements:
- name: Full official competition name
- short_name: Abbreviated name (3-4 characters, e.g., "EPL", "UCL")
- gender: Must be exactly "male" or "female" (lowercase)
- country_name: Must match one of the 60+ predefined countries (e.g., "England", "Spain", "Argentina", "Brazil", "Germany", "France", "Italy", "Netherlands", "Portugal", "Belgium", "Croatia", "Uruguay", "Colombia", "Mexico", "USA", "Japan", "South Korea", "Australia")

Output format: CSV with headers
Example:
name,short_name,gender,country_name
Premier League,EPL,male,England
UEFA Champions League,UCL,male,Europe
Liga F,LF,female,Spain`,
      downloadTemplate: downloadCompetitionTemplate,
    },
    {
      id: "venues",
      label: t("ingestion.venues"),
      description: t("ingestion.venuesDesc"),
      icon: MapPin,
      fields: ["name", "city", "country_name", "capacity", "surface"],
      aiPrompt: `Transform the following venue/stadium data into CSV format with these exact columns: name, city, country_name, capacity, surface.

Requirements:
- name: Full stadium/venue name
- city: City where venue is located
- country_name: Must match one of the 60+ predefined countries (e.g., "England", "Spain", "Argentina", "Brazil", "Germany", "France", "Italy")
- capacity: Numeric seating capacity (integer, e.g., 75000)
- surface: Must be exactly one of: "Natural Grass", "Artificial Turf", or "Hybrid"

Output format: CSV with headers
Example:
name,city,country_name,capacity,surface
Santiago Bernabéu,Madrid,Spain,81044,Hybrid
Camp Nou,Barcelona,Spain,99354,Natural Grass
Emirates Stadium,London,England,60704,Natural Grass`,
      downloadTemplate: downloadVenueTemplate,
    },
    {
      id: "referees",
      label: t("ingestion.referees"),
      description: t("ingestion.refereesDesc"),
      icon: UserIcon,
      fields: ["name", "country_name", "years_of_experience"],
      aiPrompt: `Transform the following referee data into CSV format with these exact columns: name, country_name, years_of_experience.

Requirements:
- name: Full referee name (First and Last name)
- country_name: Must match one of the 60+ predefined countries (e.g., "England", "Spain", "Argentina", "Brazil", "Germany", "France", "Italy")
- years_of_experience: Numeric value representing years of professional refereeing experience (integer, e.g., 15)

Output format: CSV with headers
Example:
name,country_name,years_of_experience
Michael Oliver,England,18
Björn Kuipers,Netherlands,20
Pierluigi Collina,Italy,28`,
      downloadTemplate: downloadRefereeTemplate,
    },
    {
      id: "players",
      label: t("ingestion.players"),
      description: t("ingestion.playersDesc"),
      icon: Users,
      fields: [
        "name",
        "position",
        "country_name",
        "birth_date",
        "player_height",
        "player_weight",
        "gender",
      ],
      aiPrompt: `Transform the following player data into CSV format with these exact columns: name, position, country_name, birth_date, player_height, player_weight, gender.

Requirements:
- name: Full player name (First and Last name)
- position: Must be exactly one of these codes: GK, CB, LB, RB, LWB, RWB, SW, CDM, CM, CAM, LM, RM, LW, RW, CF, ST, LF, RF, SS
- country_name: Must match one of the 60+ predefined countries (e.g., "England", "Spain", "Argentina", "Brazil")
- birth_date: ISO 8601 format YYYY-MM-DD (e.g., "1995-06-24")
- player_height: Height in centimeters (numeric, e.g., 180)
- player_weight: Weight in kilograms (numeric, e.g., 75)
- gender: Must be exactly "male" or "female" (lowercase)

Output format: CSV with headers
Example:
name,position,country_name,birth_date,player_height,player_weight,gender
Lionel Messi,ST,Argentina,1987-06-24,170,72,male
Alexia Putellas,CM,Spain,1994-02-04,170,65,female
Kevin De Bruyne,CM,Belgium,1991-06-28,181,70,male`,
      downloadTemplate: downloadPlayerTemplate,
    },
    {
      id: "players_with_team",
      label: "Players with Teams",
      description:
        "Create/link players into existing teams with jersey numbers.",
      icon: Users,
      fields: [
        "name",
        "position",
        "country_name",
        "birth_date",
        "player_height",
        "player_weight",
        "team_name",
        "jersey_number",
      ],
      aiPrompt: `Transform the following player-team data into CSV with these exact columns: name, position, country_name, birth_date, player_height, player_weight, team_name, jersey_number.

Requirements:
- name: Full player name (First and Last name)
- position: Must be one of GK, CB, LB, RB, LWB, RWB, SW, CDM, CM, CAM, LM, RM, LW, RW, CF, ST, LF, RF, SS
- country_name: Must match an existing country in the system
- birth_date: ISO 8601 format YYYY-MM-DD (e.g., "1995-06-24")
- player_height: Height in centimeters (numeric, e.g., 180)
- player_weight: Weight in kilograms (numeric, e.g., 75)
- team_name: Must match an existing team name already stored in the app (exact match)
- jersey_number: Player jersey number within the team (1-99)

Output format: CSV with headers
Example:
name,position,country_name,birth_date,player_height,player_weight,team_name,jersey_number
Erling Haaland,ST,Norway,2000-07-21,194,88,Manchester City,9
Alexia Putellas,CM,Spain,1994-02-04,170,58,FC Barcelona,11`,
      downloadTemplate: downloadPlayerWithTeamTemplate,
    },
    {
      id: "teams",
      label: t("ingestion.teams"),
      description: t("ingestion.teamsDesc"),
      icon: Shield,
      fields: [
        "name",
        "short_name",
        "country_name",
        "gender",
        "founded_year",
        "stadium",
        "manager_name",
      ],
      aiPrompt: `Transform the following team data into CSV format with these exact columns: name, short_name, country_name, gender, founded_year, stadium, manager_name. If a manager is provided, emit it in manager_name; the backend will map manager_name to the managers array.

Requirements:
- name: Full official team name
- short_name: Abbreviated team name (3-4 characters, e.g., "RMA", "FCB", "MCI")
- country_name: Must match one of the 60+ predefined countries (e.g., "England", "Spain", "Argentina", "Brazil")
- gender: Must be exactly "male" or "female" (lowercase)
- founded_year: Year team was founded (4-digit integer, e.g., 1902)
- stadium: Home stadium name (must match existing venue in database)
- manager_name: Current team manager/coach name (single primary manager)

Output format: CSV with headers
Example:
name,short_name,country_name,gender,founded_year,stadium,manager_name
Real Madrid,RMA,Spain,male,1902,Santiago Bernabéu,Carlo Ancelotti
FC Barcelona,FCB,Spain,female,1970,Camp Nou,Jonatan Giráldez
Manchester City,MCI,England,male,1880,Etihad Stadium,Pep Guardiola`,
      downloadTemplate: downloadTeamTemplate,
    },
  ];

  const handleImport = (modelType: ModelType) => {
    setSelectedModel(modelType);
    setShowImportDialog(true);
  };

  const handleImportSuccess = () => {
    // Refresh data or show notification
    console.log("Import successful for", selectedModel);
  };

  const handleCopyPrompt = (modelId: string, prompt: string) => {
    const payload = String(prompt || "");
    void copyTextToClipboard(payload);
    setCopiedPrompt(modelId);
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const batches = batchesData?.batches || [];
  const total = batchesData?.total || 0;
  const totalPages = Math.ceil(total / 20);

  const deletableStatuses = useMemo(() => new Set(["success", "failed"]), []);

  const canDeleteBatch = (batch: IngestionBatchSummary) => {
    if (deletableStatuses.has(batch.status)) {
      return true;
    }
    return batch.status === "conflicts" && batch.conflicts_open === 0;
  };

  const requestBatchDeletion = (batch: IngestionBatchSummary) => {
    if (!canDeleteBatch(batch)) {
      return;
    }
    setPendingDeleteBatch(batch);
  };

  const handleDeleteBatch = async () => {
    if (!pendingDeleteBatch) {
      return;
    }

    const batch = pendingDeleteBatch;
    if (!canDeleteBatch(batch)) {
      closeDeleteDialog();
      return;
    }

    try {
      setDeletingId(batch.ingestion_id);
      await deleteBatch(batch.ingestion_id);
      if (selectedBatch?.ingestion_id === batch.ingestion_id) {
        setSelectedBatch(null);
      }
      setBannerMessage({
        type: "success",
        text: t("ingestion.deleteBatchSuccess", "Batch deleted successfully."),
      });
      await refetch();
    } catch (error) {
      console.error("Failed to delete batch", error);
      setBannerMessage({
        type: "error",
        text: t(
          "ingestion.deleteBatchError",
          "We could not delete this batch.",
        ),
      });
    } finally {
      setDeletingId(null);
      closeDeleteDialog();
    }
  };

  const getStatusBadge = (status: string, batchName?: string) => {
    const statusConfig: Record<string, { cls: string; label: string }> = {
      queued: {
        cls: "bg-gray-100 text-gray-700",
        label: t("ingestion.statusLabels.queued", "Queued"),
      },
      in_progress: {
        cls: "bg-primary-100 text-primary-800",
        label: t("ingestion.statusLabels.in_progress", "Processing"),
      },
      conflicts: {
        cls: "bg-yellow-100 text-yellow-800",
        label: t("ingestion.statusLabels.conflicts", "Has Conflicts"),
      },
      failed: {
        cls: "bg-red-100 text-red-800",
        label: t("ingestion.statusLabels.failed", "Failed"),
      },
      success: {
        cls: "bg-green-100 text-green-800",
        label: t("ingestion.statusLabels.success", "Success"),
      },
    };
    const config = statusConfig[status] || statusConfig.queued;
    if (status === "in_progress") {
      return (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.cls}`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={50}
          aria-label={
            batchName
              ? `Processing ${batchName}`
              : t("ingestion.statusLabels.in_progress", "Processing")
          }
        >
          {config.label}
        </span>
      );
    }
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.cls}`}
      >
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t("ingestion.title")}
        </h1>
        <p className="text-gray-600">{t("ingestion.subtitle")}</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-0" aria-label="Tabs">
          <button
            onClick={() => setActiveTab("upload")}
            className={`${
              activeTab === "upload"
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } flex items-center gap-2 whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500`}
          >
            <Upload size={18} aria-hidden="true" />
            Upload Data
          </button>
          <button
            onClick={() => {
              setActiveTab("batches");
              refetch();
            }}
            className={`${
              activeTab === "batches"
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } flex items-center gap-2 whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500`}
          >
            <History size={18} aria-hidden="true" />
            Batches History
            {total > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium">
                {total}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Upload Tab Content */}
      {activeTab === "upload" && (
        <div>
          {/* Important Notice */}
          <div className="mb-8 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            <div className="flex items-start gap-3">
              <AlertCircle
                className="text-yellow-600 flex-shrink-0 mt-0.5"
                size={20}
                aria-hidden="true"
              />
              <div>
                <h3 className="text-sm font-semibold text-yellow-800 mb-1">
                  {t("ingestion.adminOnly")}
                </h3>
                <p className="text-sm text-yellow-800">
                  {t("ingestion.adminOnlyDesc")}
                </p>
              </div>
            </div>
          </div>

          {/* Bulk Import Card - Featured */}
          <div className="mb-8 rounded-xl border border-gray-200 bg-white shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="bg-primary-100 p-4 rounded-lg flex-shrink-0">
                <Upload
                  size={32}
                  className="text-primary-600"
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("ingestion.bulk")}
                  </h3>
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary-600 text-white">
                    NEW
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  {t("ingestion.bulkDesc")}
                </p>

                {/* AI Prompt for Bulk */}
                <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-primary-900 uppercase tracking-wide flex items-center gap-1">
                      <span aria-hidden="true">🤖</span>{" "}
                      {t("ingestion.aiPromptTitle")}
                    </h4>
                    <button
                      onClick={() =>
                        handleCopyPrompt("bulk", t("ingestion.bulkPrompt"))
                      }
                      className="inline-flex items-center justify-center gap-1 px-2 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                    >
                      {copiedPrompt === "bulk" ? (
                        <>
                          <Check size={12} aria-hidden="true" />
                          {t("ingestion.aiPromptCopied")}
                        </>
                      ) : (
                        <>
                          <Copy size={12} aria-hidden="true" />
                          {t("ingestion.aiPromptCopy")}
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-primary-800 leading-relaxed">
                    {t("ingestion.bulkTemplateNote")}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleImport("bulk" as ModelType)}
                    className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 text-base bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800"
                  >
                    <Upload size={18} aria-hidden="true" />
                    {t("ingestion.importData")}
                  </button>
                  <button
                    onClick={() => downloadBulkTemplate("csv")}
                    className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 text-base bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100"
                  >
                    <FileText size={18} aria-hidden="true" />
                    {t("ingestion.downloadTemplate")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Model Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {models.map((model) => {
              // EPIC-95 AC8: the player CSV/JSON cards lead to the new AI
              // roster importer instead of opening the CSV dialog — the
              // backend CSV pipeline stays for every other entity type.
              const isRosterImportCard =
                model.id === "players" || model.id === "players_with_team";
              return (
                <div
                  key={model.id}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm p-6 hover:shadow-md transition-shadow"
                >
                  {/* Icon and Title */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-primary-100 p-3 rounded-lg flex-shrink-0">
                      <model.icon
                        size={28}
                        className="text-primary-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {model.label}
                        </h3>
                        {isRosterImportCard && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-primary-600 text-white">
                            {t("ingestion.aiImporterBadge")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {isRosterImportCard
                          ? t("ingestion.rosterImportCardDesc")
                          : model.description}
                      </p>
                    </div>
                  </div>

                  {!isRosterImportCard && (
                    <>
                      {/* Fields */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          {t("ingestion.requiredFields")}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {model.fields.slice(0, 4).map((field) => (
                            <span
                              key={field}
                              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                            >
                              {field}
                            </span>
                          ))}
                          {model.fields.length > 4 && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              +{model.fields.length - 4} more
                            </span>
                          )}
                        </div>
                      </div>

                      {/* AI Prompt */}
                      <div className="mb-4">
                        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-primary-900 uppercase tracking-wide flex items-center gap-1">
                              <span aria-hidden="true">🤖</span>{" "}
                              {t("ingestion.aiPromptTitle")}
                            </h4>
                            <button
                              onClick={() =>
                                handleCopyPrompt(model.id, model.aiPrompt)
                              }
                              className="inline-flex items-center justify-center gap-1 px-2 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                              title={t("ingestion.aiPromptCopyTooltip")}
                            >
                              {copiedPrompt === model.id ? (
                                <>
                                  <Check size={12} aria-hidden="true" />
                                  {t("ingestion.aiPromptCopied")}
                                </>
                              ) : (
                                <>
                                  <Copy size={12} aria-hidden="true" />
                                  {t("ingestion.aiPromptCopy")}
                                </>
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-primary-800 leading-relaxed">
                            {t("ingestion.aiPromptDescription")}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() =>
                        isRosterImportCard
                          ? navigate("/admin/roster-import")
                          : handleImport(model.id)
                      }
                      data-testid={
                        isRosterImportCard
                          ? `ingestion-open-roster-importer-${model.id}`
                          : undefined
                      }
                      className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 w-full"
                    >
                      <Upload size={16} aria-hidden="true" />
                      {isRosterImportCard
                        ? t("ingestion.openRosterImporter")
                        : t("ingestion.importData")}
                    </button>
                    <button
                      onClick={() => model.downloadTemplate("csv")}
                      className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100 w-full"
                    >
                      <FileText size={16} aria-hidden="true" />
                      {t("ingestion.downloadTemplate")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Validation Rules Info */}
          <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 px-6 py-4 text-sm text-primary-800">
            <h3 className="text-lg font-semibold text-primary-900 mb-4">
              {t("ingestion.validationRules")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-primary-900 mb-2">
                  {t("ingestion.countries")}
                </h4>
                <p className="text-primary-800">
                  {t("ingestion.countriesDesc")}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary-900 mb-2">
                  {t("ingestion.playerPositions")}
                </h4>
                <p className="text-primary-800">
                  {t("ingestion.playerPositionsDesc")}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary-900 mb-2">
                  {t("ingestion.genders")}
                </h4>
                <p className="text-primary-800">{t("ingestion.gendersDesc")}</p>
              </div>
              <div>
                <h4 className="font-semibold text-primary-900 mb-2">
                  {t("ingestion.surfaces")}
                </h4>
                <p className="text-primary-800">
                  {t("ingestion.surfacesDesc")}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary-900 mb-2">
                  {t("ingestion.duplicates")}
                </h4>
                <p className="text-primary-800">
                  {t("ingestion.duplicatesDesc")}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary-900 mb-2">
                  {t("ingestion.matches")}
                </h4>
                <p className="text-primary-800">{t("ingestion.matchesDesc")}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batches History Tab Content */}
      {activeTab === "batches" && (
        <div>
          {/* Filters */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 mb-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <Select
                  value={modelFilter === "" ? UNSET : modelFilter}
                  onValueChange={(next) => {
                    setModelFilter(next === UNSET ? "" : next);
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    aria-label="Model"
                    className="w-full"
                    data-testid="ingestion-model-filter"
                  >
                    <SelectValue>
                      {MODEL_FILTER_LABELS[modelFilter]}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value={UNSET}
                      data-testid="ingestion-model-filter-option-unset"
                    >
                      All Models
                    </SelectItem>
                    <SelectItem
                      value="competitions"
                      data-testid="ingestion-model-filter-option-competitions"
                    >
                      Competitions
                    </SelectItem>
                    <SelectItem
                      value="venues"
                      data-testid="ingestion-model-filter-option-venues"
                    >
                      Venues
                    </SelectItem>
                    <SelectItem
                      value="referees"
                      data-testid="ingestion-model-filter-option-referees"
                    >
                      Referees
                    </SelectItem>
                    <SelectItem
                      value="players"
                      data-testid="ingestion-model-filter-option-players"
                    >
                      Players
                    </SelectItem>
                    <SelectItem
                      value="teams"
                      data-testid="ingestion-model-filter-option-teams"
                    >
                      Teams
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <Select
                  value={statusFilter === "" ? UNSET : statusFilter}
                  onValueChange={(next) => {
                    setStatusFilter(next === UNSET ? "" : next);
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    aria-label="Status"
                    className="w-full"
                    data-testid="ingestion-status-filter"
                  >
                    <SelectValue>
                      {STATUS_FILTER_LABELS[statusFilter]}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value={UNSET}
                      data-testid="ingestion-status-filter-option-unset"
                    >
                      All Statuses
                    </SelectItem>
                    <SelectItem
                      value="queued"
                      data-testid="ingestion-status-filter-option-queued"
                    >
                      Queued
                    </SelectItem>
                    <SelectItem
                      value="in_progress"
                      data-testid="ingestion-status-filter-option-in_progress"
                    >
                      Processing
                    </SelectItem>
                    <SelectItem
                      value="conflicts"
                      data-testid="ingestion-status-filter-option-conflicts"
                    >
                      Has Conflicts
                    </SelectItem>
                    <SelectItem
                      value="success"
                      data-testid="ingestion-status-filter-option-success"
                    >
                      Success
                    </SelectItem>
                    <SelectItem
                      value="failed"
                      data-testid="ingestion-status-filter-option-failed"
                    >
                      Failed
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => refetch()}
                  className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800"
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-500">
              Showing {batches.length} of {total} batches
            </div>
          </div>

          {/* Batches Table */}
          {batchesLoading && (
            <div
              className="rounded-xl border border-gray-200 bg-white shadow-sm p-8 text-center text-sm text-gray-500"
              role="status"
              aria-live="polite"
            >
              Loading batches...
            </div>
          )}

          {!batchesLoading && batches.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-8 text-center text-sm text-gray-500">
              No batches found. Upload your first batch using the "Upload Data"
              tab.
            </div>
          )}

          {!batchesLoading && batches.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("ingestion.batchId")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("ingestion.status")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("ingestion.conflicts")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("ingestion.created")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("ingestion.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="data-rows">
                  {batches.map((batch) => (
                    <tr
                      key={batch.ingestion_id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedBatch(batch)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="font-medium text-gray-900">
                          {batch.batch_name}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {batch.ingestion_id.slice(0, 8)}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 uppercase">
                          {t(
                            `ingestion.${batch.target_model}` as any,
                            batch.target_model,
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(batch.status, batch.batch_name)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-700">
                          <span className="text-yellow-700 font-medium">
                            {batch.conflicts_open}
                          </span>
                          {" / "}
                          <span className="text-green-700">
                            {batch.accepted}
                          </span>
                          {" / "}
                          <span className="text-red-700">{batch.rejected}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {t("ingestion.conflictsBreakdown")}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(batch.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/admin/ingestion/${batch.ingestion_id}`,
                              );
                            }}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors"
                          >
                            {t("ingestion.viewDetails")}
                            <ExternalLink size={14} aria-hidden="true" />
                          </button>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              requestBatchDeletion(batch);
                            }}
                            disabled={
                              !canDeleteBatch(batch) ||
                              deletingId === batch.ingestion_id
                            }
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded transition-colors disabled:text-gray-400 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                            title={
                              canDeleteBatch(batch)
                                ? undefined
                                : t(
                                    "ingestion.deleteBatchDisabledTooltip",
                                    "This batch cannot be removed while pending.",
                                  )
                            }
                          >
                            {deletingId === batch.ingestion_id
                              ? t("common:loading", "Loading...")
                              : t("ingestion.deleteBatch", "Delete batch")}
                            <Trash2 size={14} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Page {page} of {totalPages}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      {selectedModel && (
        <Suspense
          fallback={
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 text-white text-sm">
              {t("common:loading", "Loading...")}
            </div>
          }
        >
          <DataIngestionDialog
            modelType={selectedModel}
            isOpen={showImportDialog}
            onClose={() => {
              setShowImportDialog(false);
              setSelectedModel(null);
            }}
            onSuccess={handleImportSuccess}
            downloadTemplate={
              selectedModel === "bulk"
                ? downloadBulkTemplate
                : models.find((m) => m.id === selectedModel)
                    ?.downloadTemplate || (() => {})
            }
          />
        </Suspense>
      )}

      <BatchDetailsModal
        batch={selectedBatch}
        onClose={() => setSelectedBatch(null)}
        onOpenBatch={(ingestionId) => {
          setSelectedBatch(null);
          navigate(`/admin/ingestion/${ingestionId}`);
        }}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingDeleteBatch)}
        title={t("ingestion.deleteBatchTitle", "Remove this batch?")}
        description={
          pendingDeleteBatch
            ? t("ingestion.deleteBatchConfirm", {
                name:
                  pendingDeleteBatch.batch_name ||
                  pendingDeleteBatch.ingestion_id.slice(0, 8),
              })
            : undefined
        }
        confirmLabel={t("ingestion.deleteBatch", "Delete batch")}
        cancelLabel={t("common:cancel", "Cancel")}
        closeLabel={t("common:close", "Close")}
        confirmVariant="danger"
        isConfirming={Boolean(
          pendingDeleteBatch && deletingId === pendingDeleteBatch.ingestion_id,
        )}
        onCancel={closeDeleteDialog}
        onConfirm={handleDeleteBatch}
      />

      {bannerMessage && (
        <div
          className="fixed bottom-6 right-6 z-50"
          role="status"
          aria-live="polite"
        >
          <div
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 shadow-2xl backdrop-blur-sm transition-all duration-200 ${
              bannerMessage.type === "success"
                ? "bg-white/95 text-green-900 border-green-200"
                : "bg-white/95 text-red-900 border-red-200"
            }`}
          >
            <span className="text-sm font-medium">{bannerMessage.text}</span>
            <button
              onClick={() => setBannerMessage(null)}
              aria-label={t("common:close", "Close")}
              className="text-current hover:opacity-70"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
